import React, { useLayoutEffect, useRef } from 'react';
import * as THREE from 'three';
import { getUnitBoxGeometry } from '../../../../utils/threeResourcePool';

const UNIT_BOX = getUnitBoxGeometry();
const TEMP = new THREE.Object3D();

export type AmhsBoxInstance = {
  position: [number, number, number];
  scale: [number, number, number];
  rotation?: [number, number, number];
};

export type AmhsInstancedBatchProps = {
  instances: readonly AmhsBoxInstance[];
  material: THREE.Material;
  castShadow?: boolean;
  receiveShadow?: boolean;
};

/**
 * Shared unit-box InstancedMesh batch — one draw call per material layer.
 * Matrices written in useLayoutEffect; no per-frame allocation.
 */
export const AmhsInstancedBatch: React.FC<AmhsInstancedBatchProps> = React.memo(
  ({ instances, material, castShadow = false, receiveShadow = false }) => {
    const ref = useRef<THREE.InstancedMesh>(null);

    useLayoutEffect(() => {
      const mesh = ref.current;
      if (!mesh) return;
      for (let i = 0; i < instances.length; i += 1) {
        const inst = instances[i]!;
        TEMP.position.set(...inst.position);
        TEMP.rotation.set(...(inst.rotation ?? [0, 0, 0]));
        TEMP.scale.set(...inst.scale);
        TEMP.updateMatrix();
        mesh.setMatrixAt(i, TEMP.matrix);
      }
      mesh.count = instances.length;
      mesh.instanceMatrix.needsUpdate = true;
      mesh.computeBoundingSphere();
    }, [instances]);

    if (instances.length === 0) return null;

    return (
      <instancedMesh
        ref={ref}
        args={[UNIT_BOX, material, instances.length]}
        material={material}
        castShadow={castShadow}
        receiveShadow={receiveShadow}
        frustumCulled
        dispose={null}
      />
    );
  }
);

AmhsInstancedBatch.displayName = 'AmhsInstancedBatch';

export default AmhsInstancedBatch;
